import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { childPolicyRevivalValidationSchema } from "../../validators/LapsedPolicy.ts";
import ReviveChildPolicyForm from "./ReviveChildPolicyForm.tsx";
import {
  convertToCm,
  toastError,
  toastSuccess,
  UserToken,
} from "../../utils/helper.js";
import { usePostReviveAdultPolicy } from "../../hooks/lapsed-policy.ts";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";

type RevivePolicyResponse = {
  code?: string;
  message?: string;
  [key: string]: any;
};

const ReviveChildPolicy = ({
  policyNo,
  gender,
}: {
  policyNo: string;
  gender: string;
}) => {
  const navigate = useNavigate();
  const { mutateAsync } = usePostReviveAdultPolicy();

  const form = useForm({
    defaultValues: {
      // Child health section
      childHeight: "",
      childHeightInch: "",
      childWeight: "",
      childHadDisease: undefined,
      childHadSurgeryOrAccident: undefined,
      childHadCovid: undefined,
      childIsHealthy: undefined,

      // Proposer health section
      proposerHeight: "",
      proposerWeight: "",
      proposerHadDisease: undefined,
      proposerHadSurgeryOrAccident: undefined,
      proposerHadCovid: undefined,
      proposerIsHealthy: undefined,

      // Female proposer specific section
      isPregnant: undefined,
      hasAbortion: undefined,
      hasChildren: undefined,
      deliveryType: undefined,

      // Declaration
      declaration: undefined,
    },
    resolver: yupResolver(childPolicyRevivalValidationSchema),
    mode: "onChange",
  });

  const onSubmit = async (data: any) => {
    // const heightCm = convertToCm(data.childHeight, data.childHeightInch);

    const isMale = gender != "FEMALE";

    const filterData = {
      policyNo: policyNo,

      medicalDetail: {
        isHealthy: data.childIsHealthy.toString(),
        hasCovid: data.childHadCovid.toString(),
        hasSurgery: data.childHadSurgeryOrAccident.toString(),
        childHadDisease: data.childHadSurgeryOrAccident.toString(),
        weight: data.childWeight.toString(),
        height: data.childHeight +"."+ data.childHeightInch,
        // childHeightInch: data.childHeightInch.toString(),
      },
      proposerMedicalDetail: {
        isHealthy: data.proposerIsHealthy.toString(),
        hasCovid: data.proposerHadCovid.toString(),
        hasSurgery: data.proposerHadSurgeryOrAccident.toString(),
        proposerHadDisease: data.proposerHadDisease.toString(),
        weight: data.proposerWeight.toString(),
        height: data.proposerHeight +"."+ data.heightInch,
        hasChildren: !isMale ? data.hasChildren.toString() : undefined,
        hasAbortion: isMale ? undefined : data.hasAbortion.toString(),
        isPregnant: isMale ? undefined : data.isPregnant.toString(),
        declaration: data.declaration.toString(),
        heightInch: data.heightInch.toString(),
        hasDiseaseHistory: data.proposerHadDisease.toString(),
        hadSurgeryOrAccident: data.proposerHadSurgeryOrAccident.toString(),
        isHealthyNow: data.proposerIsHealthy.toString(),
        dateOfAbortion: isMale
          ? undefined
          : data?.dateOfAbortion
          ? data.dateOfAbortion.toString()
          : "",
        dateOfPregnancy: isMale
          ? undefined
          : data?.dateOfPregnancy
          ? data.dateOfPregnancy.toString()
          : "",
        deliveryMethod: data?.deliveryMethod ? data.deliveryMethod : "",
        deliveryType: data?.deliveryType ? data.deliveryType : "",
      },
    };

    // const formDatastringify = JSON.stringify(
    //   filterData,
    //   (key, value) => {
    //     if (typeof value === "boolean" || typeof value === "number") {
    //       return String(value); // convert to string
    //     }
    //     return value; // keep other types unchanged
    //   },
    //   2
    // );
    await UserToken();
    const response = (await mutateAsync({
      payload: filterData,
      url: "LapsedPolicy/RegisterLapsedPolicy",
    })) as RevivePolicyResponse;

    if (response?.code !== "100") {
      toastError(
        response?.message || "Failed to revive policy. Please try again."
      );
      return;
    }

    Swal.fire({
      title: "Success!",
      text: response?.message,
      icon: "success",
      confirmButtonColor: "#E51D25",
    } as any).then((result) => {
      if (result.isConfirmed) {
        navigate("/lapsed-policies");
      }
    });
  };

  return (
    <div className="max-w-4xl mx-auto p-2 lg:p-6 bg-white">
      <h4 className="text-xl font-semibold text-center underline mb-4">
        विषयः जीवन बीमालेख पुनर्जागरण गराइदिने बारे ।
      </h4>

      <div className="mb-6 text-justify leading-relaxed">
        उपरोक्त सम्बन्धमा मेरो / बीमा चाहने व्यक्तिको जीवन बीमालेखमा भुक्तानी
        गर्नुपर्ने नविकरण बीमाशुल्क विभिन्न कारणले तोकिएको समयमा भुक्तान गर्न
        नसकिएकाले बीमालेख व्यतित भएको छ। अतः सो व्यतित बीमालेख कम्पनीको
        नियमानुसार पुनर्जागरण गरिदिनुहुन अनुरोध गर्दछु।
      </div>

      <FormProvider {...form}>
        <ReviveChildPolicyForm onSubmit={onSubmit} gender={gender} />
      </FormProvider>
    </div>
  );
};

export default ReviveChildPolicy;
